import type { Reducer } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';
import { IUserSession } from '@entities/user-session';
import { WarehouseType } from '@enums/warehouse-type';
import { IDictionary } from '@core/types';

import {
    USER_LOADED,
    CURRENT_MENY_ITEM,
    WAREHOUSE_TYPE_INIT,
    SET_RESTART_SERVICE_REQUIRED,
    SET_FILTER,
    CLEANUP_STATES,
    COLUMNS_SETTINGS_INIT,
    COLUMNS_SETTINGS_VISIBILITY_CHANGED,
    COLUMNS_SETTINGS_WIDTH_CHANGED,
    COLUMNS_SETTINGS_DISPLAY_CHANGED,
    COLUMNS_SETTINGS_ORDER_CHANGED,
} from './actions';

export interface IReduxState {
    userSession?: IUserSession;
    currentMenuItem: string;
    warehouseType?: WarehouseType;
    restartServiceRequired: boolean;
    filters: IDictionary<any>;
    tableSettings: IDictionary<any>;
}

export interface IUserSettingState {
    columns: Array<any>;
    initialized: boolean;
}

export const initialState: IReduxState = {
    userSession: undefined,
    currentMenuItem: 'import',
    restartServiceRequired: false,
    warehouseType: undefined,
    filters: [],
    tableSettings: [],
};

export const reducer: Reducer = (state: any, action: AnyAction) => {
    switch (action.type) {
        case USER_LOADED:
            return {
                ...state,
                userSession: action.payload,
            };

        case WAREHOUSE_TYPE_INIT:
            return {
                ...state,
                warehouseType: action.payload,
            };

        case CURRENT_MENY_ITEM:
            return {
                ...state,
                currentMenuItem: action.payload,
            };

        case SET_RESTART_SERVICE_REQUIRED:
            return {
                ...state,
                restartServiceRequired: action.payload,
            };

        case SET_FILTER:
            return {
                ...state,
                filters: { ...state.filters, [action.context]: action.payload },
            };
        case CLEANUP_STATES:
            return {
                ...initialState,
            };

        case COLUMNS_SETTINGS_INIT: {
            const key = `${action.settingContext}.${action.settingType}`;
            let newState;

            if (action.payload.initialData) {
                newState = { ...action.payload.initialData };
            } else {
                newState = {
                    columns: [],
                    initialized: false,
                };
            }

            newState.initialized = true;

            return {
                ...state,
                tableSettings: { ...state.tableSettings, [key]: newState },
            };
        }
        case COLUMNS_SETTINGS_VISIBILITY_CHANGED: {
            const key = `${action.settingContext}.${action.settingType}`;
            const currentState = state[key];

            const index = currentState.columns.findIndex((s: any) => s.key == action.payload.name);
            const columnSettings = [...currentState.columns];

            columnSettings[index].hidden = action.payload.value;

            const newState = {
                ...currentState,
                columns: [...columnSettings],
            };

            return {
                ...state,
                tableSettings: { ...state.tableSettings, [key]: newState },
            };
        }
        case COLUMNS_SETTINGS_WIDTH_CHANGED: {
            const key = `${action.settingContext}.${action.settingType}`;
            const currentState = state[key];

            const index = currentState.columns.findIndex((s: any) => s.key == action.payload.name);
            const columnSettings = [...currentState.columns];

            columnSettings[index].width = action.payload.value;

            const newState = {
                ...currentState,
                columns: [...columnSettings],
            };

            return {
                ...state,
                tableSettings: { ...state.tableSettings, [key]: newState },
            };
        }
        case COLUMNS_SETTINGS_DISPLAY_CHANGED: {
            const key = `${action.settingContext}.${action.settingType}`;
            const currentState = state[key];

            const changedData = { [action.payload.name]: action.payload.value };

            const newState = {
                ...currentState,
                display: { ...currentState.display, ...changedData },
            };

            return {
                ...state,
                tableSettings: { ...state.tableSettings, [key]: newState },
            };
        }
        case COLUMNS_SETTINGS_ORDER_CHANGED: {
            const key = `${action.settingContext}.${action.settingType}`;
            const currentState = state[key];

            const columnSettings = [...action.payload.value];

            const newState = {
                ...currentState,
                columns: [...columnSettings],
            };

            return {
                ...state,
                tableSettings: { ...state.tableSettings, [key]: newState },
            };
        }

        default:
            return state;
    }
};
